/**
 * Create New Plugin Tab - Upload Interface
 *
 * Features:
 * - Selection screen: Upload Package
 * - ZIP file upload functionality
 */

import React, { useState } from 'react';
import {
  Button,
  Card,
  Row,
  Col,
  Tag,
  Divider,
  Alert,
  Typography,
  message
} from 'antd';
import { navigateToTab } from '../utils/navigation';
import PluginZipUpload from '../components/PluginZipUpload';

const { Title, Text, Paragraph } = Typography;

const CreateTab: React.FC = () => {
  // Tab selection (Upload only)
  const [creationMethod, setCreationMethod] = useState<'upload' | null>(null);
  
  // ============================================================================
  // Upload Handlers
  // ============================================================================

  const handleUploadSuccess = (uploadedDraftId: string, pluginName: string) => {
    console.log('Upload successful:', pluginName, uploadedDraftId);
    message.success(`Plugin "${pluginName}" uploaded successfully! View it in drafts to deploy.`);

    // Navigate to drafts tab after successful upload
    setTimeout(() => {
      navigateToTab('drafts');
    }, 2000); // Give user time to see the success message
  };
  
  const handleUploadError = (error: string) => {
    console.error('Upload error:', error);
    message.error(`Upload failed: ${error}`);
  };
  
  // ============================================================================
  // Main Render
  // ============================================================================
  
  // Selection screen - show creation method (upload)
  if (!creationMethod) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '40px 0' }}>
        {/* Header */}
        <div style={{ marginBottom: 48, textAlign: 'center' }}>
          <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
            ✨ Create New Plugin
          </Title>
          <Text type="secondary" style={{ fontSize: 16 }}>
            Choose how you want to create your plugin or browse popular categories
          </Text>
        </div>

        {/* Creation Methods */}
        <div style={{ marginBottom: 48 }}>
          <Title level={4} style={{ marginBottom: 24, textAlign: 'center' }}>
            Creation Methods
          </Title>
          <Row gutter={32} justify="center">
            {/* Upload Package Card */}
            <Col xs={24} md={10} lg={8}>
              <Card
                hoverable
                onClick={() => setCreationMethod('upload')}
                style={{
                  height: '280px',
                  cursor: 'pointer',
                  border: '2px solid #d9d9d9',
                  transition: 'all 0.3s',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                bodyStyle={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 32,
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: 16 }}>📦</div>
                <Title level={4} style={{ marginBottom: 12 }}>Upload Package</Title>
                <Paragraph style={{ fontSize: 13, color: '#595959', marginBottom: 16 }}>
                  Import a pre-built plugin package as a ZIP file. Perfect if you already have your plugin code ready.
                </Paragraph>
                <Tag color="blue" style={{ fontSize: 11, padding: '3px 10px' }}>
                  ZIP File Upload
                </Tag>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Back Button */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button 
              icon={<span>←</span>} 
              onClick={() => setCreationMethod(null)}
              size="small"
            >
              Back
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              📦 Upload Package
            </Title>
          </div>
        </div>
        <Text type="secondary">
          Upload a pre-built app package (.zip file)
        </Text>
      </div>
      
      {/* Content based on selected method */}
      {creationMethod === 'upload' && (
        /* ZIP Upload Method - Two Column Layout */
        <Row gutter={24} style={{ flex: 1, minHeight: '600px' }}>
          {/* Left: Drop Area (80%) */}
          <Col span={19}>
            <div style={{ 
              height: '100%', 
              minHeight: '600px',
              border: '3px dashed #1890ff',
              borderRadius: '12px',
              padding: '48px',
              background: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ width: '100%', maxWidth: '600px' }}>
                <PluginZipUpload
                  onUploadSuccess={handleUploadSuccess}
                  onUploadError={handleUploadError}
                />
              </div>
            </div>
          </Col>
          
          {/* Right: ZIP Structure Requirements (20%) */}
          <Col span={5}>
            <Card 
              title={
                <span style={{ fontSize: 18, fontWeight: 600, color: '#ffffff' }}>
                  📋 Required ZIP Structure
                </span>
              }
              size="small"
              style={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                border: '1px solid #2d3748',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}
              headStyle={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderBottom: '1px solid #2d3748',
                padding: '16px 20px'
              }}
              bodyStyle={{
                padding: '20px',
                background: 'transparent'
              }}
            >
              <div style={{ fontSize: 16, color: '#e2e8f0' }}>
                <div style={{ display: 'block', marginBottom: 16, color: '#cbd5e0', fontSize: 16, fontWeight: 500 }}>
                  Your ZIP file must contain:
                </div>
                
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'block', marginBottom: 8, color: '#ffffff', fontWeight: 600, fontSize: 16 }}>
                    📄 manifest.yaml (required)
                  </div>
                  <div style={{ fontSize: 14, color: '#a0aec0', lineHeight: 1.5 }}>
                    Plugin metadata with id, version, name, description
                  </div>
                </div>
                
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'block', marginBottom: 8, color: '#ffffff', fontWeight: 600, fontSize: 16 }}>
                    ⚛️ src/index.tsx or index.esm.js (recommended)
                  </div>
                  <div style={{ fontSize: 14, color: '#a0aec0', lineHeight: 1.5 }}>
                    Main React component or bundle
                  </div>
                </div>
                
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'block', marginBottom: 8, color: '#ffffff', fontWeight: 600, fontSize: 16 }}>
                    🎨 styles.css (optional)
                  </div>
                  <div style={{ fontSize: 14, color: '#a0aec0', lineHeight: 1.5 }}>
                    Custom CSS, Tailwind, or any CSS framework - loaded automatically before JS
                  </div>
                </div>
                
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'block', marginBottom: 8, color: '#ffffff', fontWeight: 600, fontSize: 16 }}>
                    📁 Additional source files (optional)
                  </div>
                  <div style={{ fontSize: 14, color: '#a0aec0', lineHeight: 1.5 }}>
                    Components, assets, etc.
                  </div>
                </div>
                
                <Divider style={{ margin: '20px 0', borderColor: '#2d3748' }} />
                
                <div style={{ 
                  background: '#1a1f2e', 
                  padding: '16px 18px', 
                  borderRadius: 8,
                  border: '1px solid #3d4556'
                }}>
                  <div style={{ display: 'block', marginBottom: 12, fontSize: 15, color: '#fbbf24', fontWeight: 600 }}>
                    💡 Example structure:
                  </div>
                  <pre style={{ 
                    margin: 0,
                    fontSize: 14,
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    color: '#34d399',
                    lineHeight: 1.8,
                    background: '#0f1419',
                    padding: '14px 16px',
                    borderRadius: 6,
                    overflow: 'auto',
                    border: '1px solid #1f2937'
                  }}>
{`my-plugin.zip
├─ manifest.yaml
├─ src/
│  └─ index.tsx
├─ styles.css (optional)
└─ README.md`}
                  </pre>
                </div>
                
                <Divider style={{ margin: '20px 0', borderColor: '#2d3748' }} />
                
                <Alert
                  message={<span style={{ fontSize: 14, color: '#ffffff' }}>Maximum file size: 50MB</span>}
                  type="info"
                  showIcon
                  style={{ 
                    fontSize: 14,
                    padding: '10px 14px',
                    background: 'rgba(24, 144, 255, 0.1)',
                    border: '1px solid rgba(24, 144, 255, 0.3)',
                    borderRadius: 6
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default CreateTab;

